import React, { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterGroup {
  id: string;
  label: string;
  type: 'checkbox' | 'radio';
  options: FilterOption[];
}

interface MapFilterProps {
  filters: FilterGroup[];
  onFilterChange: (groupId: string, selectedValues: string[]) => void;
  onClearAll?: () => void;
  position?: 'topright' | 'topleft' | 'bottomright' | 'bottomleft';
}

const MapFilter: React.FC<MapFilterProps> = ({
  filters,
  onFilterChange,
  onClearAll,
  position = 'topleft',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});

  const positionClasses = {
    topright: 'top-4 right-4',
    topleft: 'top-4 left-4',
    bottomright: 'bottom-4 right-4',
    bottomleft: 'bottom-4 left-4',
  };

  const handleFilterChange = (groupId: string, value: string, type: 'checkbox' | 'radio') => {
    setSelectedFilters(prev => {
      const currentValues = prev[groupId] || [];
      let newValues: string[];

      if (type === 'checkbox') {
        if (currentValues.includes(value)) {
          newValues = currentValues.filter(v => v !== value);
        } else {
          newValues = [...currentValues, value];
        }
      } else {
        newValues = [value];
      }

      const updated = { ...prev, [groupId]: newValues };
      onFilterChange(groupId, newValues);
      return updated;
    });
  };

  const handleClearAll = () => {
    setSelectedFilters({});
    onClearAll?.();
  };

  const hasActiveFilters = Object.values(selectedFilters).some(values => values.length > 0);

  return (
    <div className={`absolute ${positionClasses[position]} z-[1000]`}>
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 bg-white rounded-lg shadow-lg border ${
          hasActiveFilters ? 'border-blue-500' : 'border-gray-200'
        } hover:bg-gray-50 transition-colors`}
      >
        <Filter className={`w-5 h-5 ${hasActiveFilters ? 'text-blue-600' : 'text-gray-700'}`} />
      </button>

      {/* Filter Panel */}
      {isOpen && (
        <div className="absolute top-12 left-0 bg-white rounded-lg shadow-xl border border-gray-200 w-80 max-h-[80vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">ตัวกรอง (Filters)</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleClearAll}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                ล้างทั้งหมด (Clear All)
              </button>
            )}
          </div>

          <div className="p-4 space-y-4">
            {filters.map(group => (
              <div key={group.id}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">{group.label}</h4>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </div>
                <div className="space-y-2">
                  {group.options.map(option => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type={group.type}
                        name={group.id}
                        value={option.value}
                        checked={(selectedFilters[group.id] || []).includes(option.value)}
                        onChange={() => handleFilterChange(group.id, option.value, group.type)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label}</span>
                      {option.count !== undefined && (
                        <span className="text-xs text-gray-400 ml-auto">({option.count})</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapFilter;
